import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  FormLabel,
} from "@mui/material";
import GoogleMapPicker from "src/Components/GoogleMapPicker";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { fromLatLng, setKey } from "react-geocode";
import { settings } from "src/config";
import { useSnackbar } from "notistack";
import { useLoading } from "src/Hooks/useLoading";
import useSession from "src/Hooks/useSession";
import CancelIcon from "@mui/icons-material/Cancel";
import { isDeliveryInRange } from "../actions";
import { useTheme } from "@mui/material";

function AddDeliveryAddress() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useLoading();
  const {
    delivery_address,
    dispatch,
    show_delivery_dialog,
    sessionDetails: { restaurant },
  } = useSession();
  const [deliveryAddress, setDeliveryAddress] = useState(delivery_address);
  const [deliveryError, setDeliveryError] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState(null);

  const setAddressFromLatLng = (lat_Lng) => {
    if (lat_Lng) {
      setLoading(true);
      setKey(settings.GOOGLE_API_KEY);
      fromLatLng(lat_Lng?.lat, lat_Lng?.lng)
        .then(({ results }) => {
          let address_com = results?.filter(
            (res) => !res?.types?.includes("plus_code")
          );
          setDeliveryAddress({
            ...lat_Lng,
            address:
              address_com[0]?.formatted_address ||
              results[0]?.formatted_address ||
              "",
          });
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
        });
    }
  };

  const handleCurrentLocation = () => {
    if ("geolocation" in navigator) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        function (position) {
          setLoading(false);
          setDeliveryError(false);
          setDeliveryStatus(null);
          setAddressFromLatLng({
            lat: position?.coords?.latitude,
            lng: position?.coords?.longitude,
          });
        },
        function (error) {
          setLoading(false);
          enqueueSnackbar(
            error?.message || t("addDeliveryAddress.unableToRetrieveLocation"),
            {
              variant: "error",
            }
          );
        }
      );
    } else {
      enqueueSnackbar(t("addDeliveryAddress.unableToRetrieveLocation"), {
        variant: "error",
      });
    }
  };

  return (
    <>
      {show_delivery_dialog?.open && (
        <Dialog
          maxWidth="md"
          open={show_delivery_dialog?.open}
          onClose={(e, reason) => {
            if (reason === "backdropClick") {
              return;
            }
            dispatch({
              type: "CLOSE_DELIVERY_DIALOG",
            });
          }}
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center">
                <LocationOnIcon fontSize="large" />
                <Box>
                  <Typography variant="h4">{t("addDeliveryAddress.deliveryDetails")}</Typography>
                  <Typography>
                    {t("addDeliveryAddress.enterDetailsToCheckDeliveryServices")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={() =>
                  dispatch({
                    type: "CLOSE_DELIVERY_DIALOG",
                  })
                }
                color="black"
              >
                <CancelIcon></CancelIcon>
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <>
              <Divider />
              <GoogleMapPicker
                latLng={
                  Object.keys(deliveryAddress)?.length
                    ? {
                        lat: deliveryAddress?.lat,
                        lng: deliveryAddress?.lng,
                      }
                    : null
                }
                handleChange={(value) => {
                  setDeliveryError(false);
                  setDeliveryStatus(null);
                  if (value?.address) {
                    setDeliveryAddress(value);
                  } else {
                    setAddressFromLatLng(value);
                  }
                }}
                inputProps={{
                  placeholder: t("addDeliveryAddress.searchForAnAddress"),
                  fullWidth: true,
                }}
              />
              {deliveryAddress?.lat && (
                <Typography
                  display="flex"
                  alignItems="center"
                  component="div"
                  pt={1}
                  color={!deliveryAddress?.lat && "error"}
                >
                  <LocationOnIcon />
                  {deliveryAddress?.address}
                </Typography>
              )}

              <Button
                sx={{ px: 0, my: 1 }}
                onClick={handleCurrentLocation}
                variant="text"
              >
                <MyLocationIcon /> {t("addDeliveryAddress.useMyCurrentLocation")}
              </Button>

              {deliveryError && !deliveryStatus?.data?.data
                        ?.is_delivery_available &&
                (
                  <Box>
                    <FormLabel
                      sx={{
                        color: theme.palette.error.main,
                      }}
                    >
                      {deliveryStatus?.data?.data
                        ?.delivery_status_description || t("addDeliveryAddress.weDontDeliverToGivenAddress")}
                    </FormLabel>
                  </Box>
                )}

              <Button
                disabled={!deliveryAddress?.lat}
                onClick={() => {
                  setLoading(true);
                  isDeliveryInRange({
                    property_id: restaurant?.property_id,
                    latitude: deliveryAddress?.lat,
                    longitude: deliveryAddress?.lng,
                  })
                    .then((res) => {
                      setLoading(false);
                      setDeliveryStatus(res);

                      if (res?.data?.data?.is_delivery_available) {
                        dispatch({
                          type: "SET_DELIVERY_ADDRESS",
                          payload: {
                            delivery_address: {
                              ...deliveryAddress,
                              deliveryInfo: res.data.data,
                            },
                          },
                        });
                        show_delivery_dialog?.onLocationSelect &&
                          show_delivery_dialog?.onLocationSelect(
                            deliveryAddress
                          );
                        setDeliveryError(false);
                        dispatch({
                          type: "CLOSE_DELIVERY_DIALOG",
                        });

                        enqueueSnackbar(t("addDeliveryAddress.deliveryLocationConfirmed"), {
                          variant: "success",
                        });
                      } else {
                        setDeliveryError(true);
                        enqueueSnackbar(
                          res?.data?.data?.delivery_status_description ||
                            t("addDeliveryAddress.deliveryNotAvailableForThisLocation"),
                          {
                            variant: "error",
                          }
                        );
                      }
                    })
                    .catch((error) => {
                      setLoading(false);
                      setDeliveryError(true);
                      enqueueSnackbar(t("addDeliveryAddress.errorCheckingDeliveryAvailability"), {
                        variant: "error",
                      });
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
                sx={{ float: "right", my: 1 }}
              >
                {t("addDeliveryAddress.confirmLocation")}
              </Button>
            </>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default AddDeliveryAddress;
